import {
    NewsletterResponse,
    Newsletter
} from '@App/libs/interfaces/Newsletters'
import {
    API_COBERTURAS,
    ApiResponseCoberturasEspeciais
} from '@App/libs/interfaces/PRO.interfaces'
import {
    API_JOTA_CMS,
    apiAlertaBySlug,
    apiAlertasSuffix
} from '@App/libs/utils/constants/global'
import { MATINAL_ID, VERTICAL } from '@App/libs/utils/constants/pro'
import {
    AlertaCMSList,
    AlertaContent,
    getToken,
    removeSpecialChars
} from '@App/libs/utils/geral'
import axios from 'axios'

axios.defaults.baseURL = process.env.NEXT_PUBLIC_BACKOFFICE_API
axios.defaults.headers.post['Content-Type'] = 'application/json'

export const axiosJOTA = axios.create({
    baseURL: process.env.NEXT_PUBLIC_BACKOFFICE_API
})

/**
 * Profile User API
 * Me - Return data from the user
 */
export const ProfileAPI = {
    /**
     * Generates the URL for the current user's profile in the backoffice API.
     * This URL is constructed using the NEXT_PUBLIC_BACKOFFICE_API environment variable.
     *
     * @return {string} The URL for the current user's profile.
     * @throws {Error} Throws an error if NEXT_PUBLIC_BACKOFFICE_API is not defined.
     */
    me: () =>
        `${process.env.NEXT_PUBLIC_BACKOFFICE_API}/api/auth/users/profiles/me/`,
    resetPassword: (email: string) =>
        `${process.env.REACT_APP_BO_API}/auth/users/forgot-password?email=${email}&context=pro`,
    recentNewsletter: (page = '1', limit = '3') =>
        `${process.env.NEXT_PUBLIC_BACKOFFICE_API}/newsletters/campaigns/latest/?page=${page}&limit=${limit}&exclude=matinal`
}

/**
 * JOTA PRO Api
 * Return data from JOTA PRO Content
 * @param {string} slug
 * @param {string} token
 * @return {Promise}
 */
export const jotaPROApi = (slug: string, token: string) => {
    return new Promise((resolve, reject) => {
        axiosJOTA
            .get(`${process.env.NEXT_PUBLIC_PRO_NEWSLETTERS}/slug/${slug}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            .then((resp) => resolve(resp))
            .catch((error) => reject(error))
    })
}

export const getLastPRONews = () => {
    return new Promise((resolve, reject) => {
        axiosJOTA
            .post(`${process.env.NEXT_PUBLIC_OPENSEARCH}`, {
                from: 0,
                size: 4,
                query: {
                    match: {
                        'source.type': 'cms.newsletter'
                    }
                },
                sort: [
                    {
                        'source.published_at': {
                            order: 'desc'
                        }
                    }
                ]
            })
            .then((resp) => resolve(resp))
            .catch((error) => reject(new Error(error.message)))
    })
}

/**
 * Get Last Content from JOTA PRO by OpenSearch
 * @param {string} type
 * @param {number} size
 * @return {Promise}
 */
export const getLastProcontent = (type: string, size = 4) => {
    return new Promise((resolve, reject) => {
        const OPENSEARCH_API = process.env.NEXT_PUBLIC_OPENSEARCH
        const OPENSEARCH_TOKEN = process.env.NEXT_PUBLIC_OPENSEARCH_TOKEN

        type = removeSpecialChars({ str: type })

        if (!OPENSEARCH_API || !OPENSEARCH_TOKEN) return

        const query = {
            from: 0,
            size: size,
            query: {
                bool: {
                    filter: [
                        { term: { 'source.type': 'cms.newsletter' } },
                        { term: { 'vertical.slug': type } }
                    ],
                    must_not: [
                        {
                            bool: {
                                must: [
                                    { term: { 'categories.id': MATINAL_ID } },
                                    {
                                        term: {
                                            'vertical.slug':
                                                VERTICAL.TRIBUTOS.toLowerCase()
                                        }
                                    }
                                ]
                            }
                        },
                        {
                            bool: {
                                must: [
                                    { term: { 'categories.slug': 'matinal' } },
                                    { term: { 'vertical.slug': 'saude' } }
                                ]
                            }
                        }
                    ]
                }
            },
            sort: [
                {
                    'source.published_at': {
                        order: 'desc'
                    }
                }
            ]
        }

        axiosJOTA
            .post(OPENSEARCH_API, query, {
                headers: { Authorization: OPENSEARCH_TOKEN }
            })
            .then((response) => resolve(response.data.hits.hits))
            .catch((error) => reject(new Error(error.message)))
    })
}

/**
 * Get content by term from OpenSearch or Wordpress API
 * @param {string} term
 * @param {number} page
 * @return {Promise}
 */
export const getPostsByTypeContent = async (term: string, page = 1) => {
    try {
        const url = `${process.env.NEXT_PUBLIC_JOTAINFO}/wp-json/ju/v1/posts?category=${term}&page=${page}&per_page=10`
        const response = await fetch(url)
        const posts = await response.json()

        return { posts, type: 'info' }
    } catch (error: any) {
        return Promise.reject(new Error(error))
    }
}

/**
 * Get content from OpenSearch
 * by term, page and contentType
 * @param {string} term
 * @param {string} termPrettyName
 * @param {number} page
 * @param {number} size
 * @param {verticalSlug | null} vertical
 */
export const getContentFromOpenSearch = async (
    term: string,
    termPrettyName: string,
    page = 1,
    size = 10,
    vertical = null
) => {
    return new Promise((resolve, reject) => {
        if (
            !process?.env?.NEXT_PUBLIC_OPENSEARCH ||
            !process?.env?.NEXT_PUBLIC_OPENSEARCH_TOKEN
        ) {
            reject(new Error('OS failed'))
            return
        }

        const OS_STEP_1_PAGINATION = {
            from: (page - 1) * size,
            size: size
        }

        const OS_STEP_2_QUERY = {
            query: {
                bool: {
                    should: [
                        {
                            bool: {
                                filter: [
                                    {
                                        multi_match: {
                                            query: termPrettyName,
                                            type: 'phrase',
                                            fields: [
                                                'title.headline',
                                                'title.hat',
                                                'title.subhead',
                                                'content.plain'
                                            ]
                                        }
                                    },
                                    {
                                        match: {
                                            'source.type': 'cms.newsletter'
                                        }
                                    },
                                    ...(vertical !== null
                                        ? [
                                              {
                                                  match: {
                                                      'vertical.slug': vertical
                                                  }
                                              }
                                          ]
                                        : [])
                                ]
                            }
                        },
                        {
                            bool: {
                                filter: [
                                    {
                                        match: {
                                            'source.type': 'wp.post'
                                        }
                                    },
                                    {
                                        match: {
                                            'categories.slug.keyword': term
                                        }
                                    },
                                    {
                                        term: {
                                            'source.recycled': false
                                        }
                                    }
                                ]
                            }
                        }
                    ],
                    minimum_should_match: 1
                }
            }
        }

        const OS_STEP_3_ORDER = {
            sort: [
                {
                    'source.published_at': {
                        order: 'desc'
                    },
                    _score: {
                        order: 'desc'
                    }
                }
            ]
        }

        const OS_STEP_4_HIGHLIGHT = {
            highlight: {
                pre_tags: [''],
                post_tags: [''],
                fields: {
                    'content.plain': {}
                }
            }
        }

        const OS_QUERIE = {
            ...OS_STEP_1_PAGINATION,
            ...OS_STEP_2_QUERY,
            ...OS_STEP_3_ORDER,
            ...OS_STEP_4_HIGHLIGHT
        }

        const searchDataAPI = fetch(process?.env?.NEXT_PUBLIC_OPENSEARCH, {
            method: 'post',
            headers: new Headers({
                Authorization: process.env?.NEXT_PUBLIC_OPENSEARCH_TOKEN || '',
                'Content-Type': 'application/json'
            }),
            body: JSON.stringify(OS_QUERIE)
        })

        searchDataAPI
            .then((response) => resolve(response.json()))
            .catch((error) => reject(new Error(error)))
    })
}
/**
 * Fetch Alertas API
 * @param {number} page
 * @return {Promise<AlertaContent[]>}
 */
export const fetchAlertas = async (
    page: number
): Promise<{ results: AlertaContent[]; total: number }> => {
    return new Promise<{ results: AlertaContent[]; total: number }>(
        (resolve, reject) =>
            (async () => {
                try {
                    if (!API_JOTA_CMS) {
                        reject(new Error('API_JOTA_CMS is not defined'))
                    }
                    const response = await fetch(
                        `${API_JOTA_CMS}${apiAlertasSuffix(10, page)}`
                    )
                    const data: Pick<AlertaCMSList, 'results' | 'count'> =
                        await response.json()

                    const alertas: AlertaContent[] = data.results.map(
                        (apiResponse: any) => {
                            const {
                                id,
                                published_at: publishedAt,
                                status,
                                title,
                                content,
                                date,
                                autor,
                                assets,
                                tags,
                                type,
                                slug,
                                segmentations
                            } = apiResponse

                            return {
                                id,
                                publishedAt,
                                status,
                                title,
                                content,
                                date,
                                autor,
                                assets,
                                tags,
                                type,
                                slug,
                                segmentations
                            }
                        }
                    )

                    const output = {
                        results: alertas,
                        total: data.count
                    }

                    resolve(output)
                } catch (error) {
                    reject(
                        new Error(
                            error instanceof Error
                                ? error.message
                                : String(error)
                        )
                    )
                }
            })()
    )
}

/**
 * Fetch Alert by slug
 * @param {string} slugAlert
 * @param {string} token
 * @return {Promise<AlertaContent>}
 */
export const fetchAlertaBySlug = async (
    slugAlert: string,
    token: string
): Promise<AlertaContent> => {
    return new Promise<AlertaContent>((resolve, reject) =>
        (async () => {
            try {
                if (!API_JOTA_CMS) {
                    throw new Error('API_JOTA_CMS is not defined')
                }

                const response = await fetch(
                    `${API_JOTA_CMS}${apiAlertaBySlug(slugAlert)}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                )

                const data: any = await response.json()

                const {
                    id,
                    published_at: publishedAt,
                    status,
                    title,
                    content,
                    date,
                    autor,
                    tags,
                    type,
                    assets,
                    slug,
                    segmentations
                } = data

                resolve({
                    id,
                    publishedAt,
                    status,
                    title,
                    content,
                    date,
                    autor,
                    tags,
                    type,
                    assets,
                    slug,
                    segmentations
                })
            } catch (error) {
                reject(
                    new Error(
                        error instanceof Error ? error.message : String(error)
                    )
                )
            }
        })()
    )
}

/**
 * Get Data from Coberturas Especiais Child
 * @param {string} slug - The slug of the cobertura
 * @param {number} pageFromParams - The page parameter
 * @return {Promise<ApiResponseCoberturasEspeciais>} A promise that resolves to the fetched data or null in case of an error
 */
export const getDataFromCoberturasEspeciaisChild = async (
    slug: string,
    pageFromParams: number
) => {
    if (!slug || typeof slug !== 'string') {
        throw new Error('Parâmetro "slug" inválido.')
    }

    if (!Number.isInteger(pageFromParams) || pageFromParams < 1) {
        throw new Error(
            'Parâmetro "pageFromParams" deve ser um número inteiro positivo.'
        )
    }

    return new Promise<ApiResponseCoberturasEspeciais>(
        async (resolve, reject) => {
            try {
                const fetchData = await fetch(
                    `${API_COBERTURAS}/${slug}?page=${pageFromParams}`
                )

                const data = await fetchData.json()

                if (
                    !data ||
                    typeof data !== 'object' ||
                    !data.header ||
                    !data.content ||
                    !Array.isArray(data.content.posts)
                ) {
                    throw new Error('Resposta da API está malformada.')
                }

                resolve(data as ApiResponseCoberturasEspeciais)
            } catch (error) {
                reject(
                    error instanceof Error ? error : new Error(String(error))
                )
            }
        }
    )
}

/**
 * Get a specific Post PRO from OpenSearch
 * by slug, category and vertical
 * @param {string} slug
 * @param {string} categorySlug
 * @param {string} verticalSlug
 */
export const getPostPROFromOpenSearch = async (
    slug: string,
    categorySlug: string,
    verticalSlug: string
) => {
    return new Promise((resolve, reject) => {
        if (
            !process?.env?.NEXT_PUBLIC_OPENSEARCH ||
            !process?.env?.NEXT_PUBLIC_OPENSEARCH_TOKEN
        ) {
            reject(new Error('OpenSearch API is not defined'))
            return
        }

        const isAlert = categorySlug === 'alertas'
        const sourceType = isAlert ? 'cms.alert' : 'cms.newsletter'

        const mustClauses: Array<{ term: Record<string, any> }> = [
            {
                term: {
                    'source.slug.keyword': slug
                }
            },
            {
                term: {
                    'vertical.slug.keyword': !isAlert ? verticalSlug : 'alerta'
                }
            },
            {
                term: {
                    'source.type.keyword': sourceType
                }
            }
        ]

        if (!isAlert) {
            mustClauses.push({
                term: {
                    'categories.slug.keyword': categorySlug
                }
            })
        }

        const query = {
            size: 1,
            query: {
                bool: {
                    must: mustClauses
                }
            }
        }

        fetch(process.env.NEXT_PUBLIC_OPENSEARCH, {
            method: 'POST',
            headers: new Headers({
                Authorization: process.env.NEXT_PUBLIC_OPENSEARCH_TOKEN || '',
                'Content-Type': 'application/json'
            }),
            body: JSON.stringify(query)
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`)
                }
                return response.json()
            })
            .then((data) => resolve(data))
            .catch((error) =>
                reject(
                    error instanceof Error ? error : new Error(String(error))
                )
            )
    })
}

export const API_HEADER_FOOTER = process.env.NEXT_PUBLIC_HEADER_AND_FOOTER
export const API_AUTHOR = process.env.NEXT_PUBLIC_POSTS_BY_AUTHOR
export const API_TAG = process.env.NEXT_PUBLIC_POSTS_BY_TAG
export const API_CATEGORIA = process.env.NEXT_PUBLIC_CATEGORIAS
export const API_POST_SPECIAL = process?.env?.NEXT_PUBLIC_SPECIAL_NEWS
export const API_COBERTURAS_ESPECIAIS = process?.env?.NEXT_PUBLIC_API_COBERTURAS
export const HOURS24 = 60 * 60 * 24

/**
 * Get newsletters selected by user
 * @return {Promise<any>} A promise that resolves to the newsletters selected by the user
 * @throws {Error} Throws an error if the request fails
 */
export const getNewslettersSelectedByUser =
    async (): Promise<NewsletterResponse> => {
        const token = (await getToken()) as string
        const URL = `${process.env.NEXT_PUBLIC_BACKOFFICE_API}/newsletters/optin/`
        const response = await axios.get<NewsletterResponse>(URL, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
    }

/**
 * Newsletters grouped by vertical
 * @param {NewsletterResponse} data - The newsletters data to group
 * @return {Record<string, Newsletter[]>} An object where keys are vertical names and values
 */
export const groupNewslettersByVertical = (
    data: NewsletterResponse
): Record<string, Newsletter[]> => {
    return data.results.reduce(
        (acc, newsletter) => {
            const verticalName = newsletter.vertical?.name || 'Outros'
            if (!acc[verticalName]) {
                acc[verticalName] = []
            }
            acc[verticalName].push(newsletter)
            return acc
        },
        {} as Record<string, Newsletter[]>
    )
}

/**
 * Newsletter Subscribe ou Unsubscribe
 * @param {number} newsletterId - ID da newsletter.
 * @param {string} action - Ação a ser realizada: 'subscribe' ou 'unsubscribe'.
 * @return {Promise<void>} Promise que resolve quando a ação for concluída.
 */
export const updateNewsletterSubscription = async (
    newsletterId: number,
    action: 'subscribe' | 'unsubscribe'
): Promise<void> => {
    const token = (await getToken()) as string
    const URL = `${process.env.NEXT_PUBLIC_BACKOFFICE_API}/newsletters/optin/${newsletterId}/${action}/`

    try {
        await axios.patch(
            URL,
            {},
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json, text/plain, */*'
                }
            }
        )
    } catch (error) {
        throw new Error(`Erro ao atualizar a inscrição: ${error}`)
    }
}

/**
 * Ativa ou pausa *todas* as newsletters do usuário.
 * @param {'subscribe' | 'unsubscribe'} action - Ação desejada.
 * @return {Promise<void>}
 * @throws {Error} Quando a request falhar.
 */
export const updateAllNewsletterSubscriptions = async (
    action: 'subscribe' | 'unsubscribe'
): Promise<void> => {
    const token = (await getToken()) as string

    const URL = `${process.env.NEXT_PUBLIC_BACKOFFICE_API}/newsletters/optin/all/${action}/`

    try {
        await axios.patch(
            URL,
            {},
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json, text/plain, */*'
                }
            }
        )
    } catch (error) {
        throw new Error(
            `Erro ao ${action === 'subscribe' ? 'ativar' : 'pausar'} todas as newsletters: ${error}`
        )
    }
}
